const STORAGE_KEY = 'taskonio_github_install_origin_org';

/**
 * Remembers which organization a GitHub install was started from, so the callback
 * screen can stop asking a question the user already answered.
 *
 * DELIBERATELY CLIENT-SIDE ONLY. The server has the same fact - `issueInstallState`
 * signs `{ organizationId, userId }` into the install `state` - but it refuses to let
 * that value reach the authenticated half of the flow: `GET /setup` treats it as
 * advisory and never reads it for authorization, and `parkPendingInstall` asserts the
 * parked record has exactly four keys precisely so an organizationId cannot be smuggled
 * across that boundary. Routing the hint through the browser instead keeps that airgap
 * intact: this value never reaches the server, so it cannot influence a write even in
 * principle. The confirm still takes its organization from a guarded route param.
 *
 * Consequently this is a HINT, not an authorization. The callback screen must still
 * check the value against the organizations the user actually administers, and must
 * still require a click - a tampered value can at worst preselect an organization the
 * user already administers.
 *
 * sessionStorage rather than localStorage: the hint is meaningful only for the redirect
 * round trip that is in flight, and it survives one because GitHub returns to the same
 * tab. Every access is guarded - private-mode and blocked-storage browsers throw on
 * access, and the picker is the correct fallback whenever this returns null.
 */
export function rememberInstallOriginOrg(organizationId: number): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(organizationId));
  } catch {
    // Storage unavailable. The callback screen falls back to the picker.
  }
}

export function readInstallOriginOrg(): number | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    // Number('') is 0 and Number(' ') is 0, so a blank value would otherwise read as a
    // legitimate organization id.
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function forgetInstallOriginOrg(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do - a stale hint is only ever a preselection.
  }
}
